import React from 'react';
import HeroPlaceholder from '../components/HeroPlaceholder';
import FaqAccordion from '../components/FaqAccordion';
import CtaBand from '../components/CtaBand';
import SEO from '../components/SEO';
import AeoCallout from '../components/AeoCallout';
import RelatedBand from '../components/RelatedBand';
import MattingCalculator from '../components/MattingCalculator';
import { HERO_MEDIA } from '../config/media';
import { usePageContent } from '../lib/useContent';
import {
    orgJsonLd,
    breadcrumbJsonLd,
    faqJsonLd,
} from '../lib/seo';

// Frozen inline fallback - matches the current page JSX verbatim.
const REN_FALLBACK = {
    hero: {
        eyebrow: 'Wind & Renewables',
        headline: 'Crane pads and access roads for wind farm and solar construction.',
        body: 'Wind farm construction puts extraordinary demand on ground protection. Centurion crane mats and access mats deliver the foundation utilities and EPCs need across Western Canada - Manufacturer Direct from BC.',
        image_url: HERO_MEDIA.windFarmConstruction.src,
    },
    applications: {
        items: [
            {
                title: 'Wind turbine crane pads',
                body: 'Custom timber crane mats engineered to the rigging plan. Recessed spot-welded nuts to resist vibration loosening across the lift duration. Coastal Fir #2 & BTR.',
            },
            {
                title: 'Wind farm access roads',
                body: 'Traditional 8\u2032\u00D714\u2032\u00D75\u2033 access mats for component delivery on remote ridges. Spreads tracked- and wheeled-load across muskeg, frozen ground, and reclaimed access roads.',
            },
            {
                title: 'Transmission line construction',
                body: 'Stable access for line construction, tower assemblies, and remote pull sites - built for the dispersed footprint of T&D work across BC and Alberta.',
            },
            {
                title: 'Solar facility staging + tracker pads',
                body: 'Access matting for module yards and tracker installation pads. Engineered transitions onto and off of mat corridors via ramp mats.',
            },
        ],
    },
    faq: {
        items: [
            {
                q: 'What size crane mats are used in wind farm construction?',
                a: 'Most wind farm crane lifts use 4\u2032\u00D720\u2032\u00D712\u2033 or 4\u2032\u00D730\u2032\u00D712\u2033 timber crane mats, with thickness specified by the rigging engineer based on crane footprint, allowable ground-bearing pressure, and underlying conditions. Centurion builds custom sizes to engineered specifications at our Chemainus, BC mill.',
            },
            {
                q: 'Can Centurion build custom crane mats for specific lift specs?',
                a: 'Yes. Our Chemainus, BC mill regularly builds to non-standard sizes and bolt patterns when the lift plan calls for it. Send the rigging spec to sales and we\u2019ll quote - widths from 2\u2032 to 10\u2032, lengths from 4\u2032 to 40\u2032, thicknesses from 6\u2033 to 18\u2033.',
            },
            {
                q: 'How are mats deployed on remote wind farm ridges?',
                a: 'Highway haul to staging, then off-highway transport into the ridge. Centurion coordinates the milling schedule with the wind farm construction milestones so mat deliveries align with crane crew arrivals. Multi-deployment across pad locations is standard.',
            },
            {
                q: 'What\u2019s the typical lead time for custom crane mats?',
                a: 'Lead time aligns with mill schedule and crane lift milestone - bring the rigging spec early in the project planning phase and Centurion will time the build to match the construction sequence. Manufacturer Direct from BC.',
            },
            {
                q: 'Does Centurion support transmission line / solar projects too?',
                a: 'Yes. Transmission and solar build out the same family of access and crane mats - access matting for tower-pull and module-yard work, custom crane mats for transformer set, tower assembly, and component placement.',
            },
        ],
    },
};

export default function Renewables() {
    const content = usePageContent('projects.renewables', REN_FALLBACK);
    const hero = content.hero || REN_FALLBACK.hero;
    const applications = (content.applications?.items?.length ? content.applications.items : REN_FALLBACK.applications.items);
    const faqs = (content.faq?.items?.length ? content.faq.items : REN_FALLBACK.faq.items);

    return (
        <>
            <SEO
                title="Wind & Renewables Matting - Crane Pads + Access Roads"
                description="Centurion crane mats and access mats for wind farm, solar, and transmission line construction across Western Canada. Custom timber crane mats built to your rigging spec - Manufacturer Direct."
                cmsSlug="seo.projects.renewables"
                path="/projects/renewables"
                image={HERO_MEDIA.windFarmConstruction.src}
                jsonLd={[
                    orgJsonLd(),
                    breadcrumbJsonLd([
                        { name: 'Home', path: '/' },
                        { name: 'Projects', path: '/projects' },
                        { name: 'Wind & Renewables', path: '/projects/renewables' },
                    ]),
                    faqJsonLd(faqs),
                    {
                        '@context': 'https://schema.org',
                        '@type': 'Service',
                        serviceType: 'Wind, Solar & Transmission Matting Supply',
                        provider: { '@type': 'Organization', name: 'Centurion Matting Products' },
                        areaServed: 'Western Canada',
                        url: 'https://centurionmatting.com/projects/renewables',
                    },
                ]}
            />

            <HeroPlaceholder
                mediaKey="windFarmConstruction"
                imageUrl={hero.image_url}
                eyebrow={hero.eyebrow}
                title={hero.headline}
                subtitle={hero.body}
            />

            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">Overview</span>
                <p className="mt-8 font-sans text-lg leading-relaxed text-brand-text">
                    Wind farm construction places extraordinary demand on ground protection: crane mats must support hundreds of tonnes during turbine assembly, and access roads must survive heavy component delivery on remote ridges. Centurion crane mats (2-10&prime; wide &times; 4-40&prime; long &times; 6-18&Prime; thick, custom sizes built to order) and access mats deliver the foundation utilities and EPCs need.
                </p>
                <p className="mt-6 font-sans text-base leading-relaxed text-brand-muted">
                    From transmission line construction to solar tracker installation pads, Centurion’s manufacturer-direct supply from our Chemainus, BC mill (with sales coordination from Calgary, AB) means owners, contractors, and First Nations partners can move at project velocity, on schedule and on budget.
                </p>
            </section>

            {/* Application grid */}
            <section className="border-y border-brand-hairline bg-brand-surface" data-eyebrow-tone="blue">
                <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24">
                    <span className="eyebrow">Where Centurion Mats Work</span>
                    <h2 className="h-display mt-6 max-w-3xl text-3xl sm:text-4xl">
                        Renewables, transmission, and solar - one supplier.
                    </h2>
                    <div className="mt-12 grid gap-px bg-brand-hairline md:grid-cols-2" data-testid="ren-applications">
                        {applications.map((a, i) => (
                            <div key={a.title} className="bg-brand-surface p-6 md:p-8">
                                <span className="font-display text-xs font-bold tabular-nums tracking-[0.2em] text-brand-gold">
                                    0{i + 1}
                                </span>
                                <h3 className="mt-5 font-display text-lg font-bold uppercase tracking-tight text-brand-text">
                                    {a.title}
                                </h3>
                                <p className="mt-3 font-sans text-sm leading-relaxed text-brand-muted">
                                    {a.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Spec callout */}
            <section className="mx-auto max-w-4xl px-6 py-20 md:py-24">
                <AeoCallout
                    testId="aeo-ren-spec"
                    q="Typical crane mat sizes for wind farm construction?"
                    a={'Most wind farm crane lifts use 4\u2032\u00D720\u2032\u00D712\u2033 or 4\u2032\u00D730\u2032\u00D712\u2033 timber crane mats. Centurion builds custom sizes to engineered specs with recessed spot-welded nuts to resist vibration. Final thickness is set by the rigging plan and underlying ground conditions.'}
                />
            </section>

            {/* Calculator - compact, access-corridor estimator */}
            <section className="border-y border-brand-hairline bg-brand-surface" id="calculator">
                <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-24">
                    <span className="eyebrow">Field Calculator</span>
                    <h2 className="h-display mt-6 max-w-3xl text-3xl sm:text-4xl">
                        Access corridor estimator for component delivery.
                    </h2>
                    <p className="mt-5 max-w-2xl font-sans text-base text-brand-muted">
                        Enter route distance and corner count to size the mat order for your access roads.
                    </p>
                    <div className="mt-10">
                        <MattingCalculator variant="compact" />
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
                <span className="eyebrow">FAQ</span>
                <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                    Common questions about renewables matting.
                </h2>
                <div className="mt-10">
                    <FaqAccordion faqs={faqs} testId="faq-ren" />
                </div>
            </section>

            <RelatedBand
                heading="Adjacent project hubs."
                items={[
                    { to: '/projects', eyebrow: 'Portfolio', title: 'All Projects', body: 'Pipeline, infrastructure, wind, and partnerships in one view.' },
                    { to: '/projects/infrastructure', eyebrow: 'Infrastructure', title: 'Infrastructure Projects', body: 'Power & transmission, civil, utility - engineered ground protection.' },
                    { to: '/projects/oil-gas-pipeline', eyebrow: 'Pipeline', title: 'Oil & Gas / Pipeline', body: 'Well-site access, mainline corridors, tank-farm staging.' },
                ]}
            />

            <CtaBand
                eyebrow="Manufacturer Direct"
                title="Spec your wind, solar, or transmission matting package."
                body="Custom crane mats and access matting from BC mills, dispatched across Western Canada."
                cta={{ to: '/quote?mat_type=crane&project_type=wind_solar', label: 'Get a Manufacturer Direct Quote' }}
                secondary={{ to: '/contact', label: 'Talk to Sales' }}
            />
        </>
    );
}
